import { COMPONENTS, type ISpecComponents } from '../spec/SpecComponents';

/**
 * Class for managing FIX protocol components.
 * Provides functionality to find and process FIX components,
 * including looking up components by ID or name and retrieving
 * associated field tags.
 */
export class Components {
    /** Array of all available component specifications */
    public components: ISpecComponents[] = COMPONENTS;
    /** Cache map for looking up components by ID */
    public cacheMap = new Map<number, ISpecComponents>();
    /** Cache map for looking up components by name */
    public cacheMapByName = new Map<string, ISpecComponents>();

    /**
     * Creates a new Components instance and initializes the cache maps
     * with all available component specifications.
     */
    constructor() {
        this.components.forEach((component) => {
            this.cacheMap.set(component.id, component);
        });
        this.components.forEach((component) => {
            this.cacheMapByName.set(component.name, component);
        });
    }

    /**
     * Finds a component by its ID.
     *
     * @param {number} componentId - The ID of the component to find
     * @returns {ISpecComponents | undefined} The found component, or undefined if not found
     */
    public find(componentId: number): ISpecComponents | undefined {
        return this.cacheMap.get(componentId);
    }

    /**
     * Finds a component by its name.
     *
     * @param {string} name - The name of the component to find
     * @returns {ISpecComponents | undefined} The found component, or undefined if not found
     */
    public findByName(name: string): ISpecComponents | undefined {
        return this.cacheMapByName.get(name);
    }

    /**
     * Gets all field tags associated with a component by its name.
     *
     * @param {string} name - The name of the component
     * @returns {number[]} An array of field tag numbers associated with the component
     */
    public getTagsByComponentName(name: string): number[] {
        const tags: number[] = [];
        const component = this.findByName(name);
        if (component) {
            component.fieldRef?.forEach((field) => {
                tags.push(field.id);
            });
        }
        return tags;
    }
}
